import { useState } from "react";
import { SiQuickbooks } from "react-icons/si";
// Local
import { createToastMessageID } from "../../utils";
import * as strings from "../../strings";
import { useToastMessageStore } from "../../store/toast-messages";
import { DbWrite } from "../../database";
import { logger } from "../../logging";
import BaseButtonSecondary from "../BaseButtonSecondary";
import ButtonColored from "../ButtonColored";
import ButtonConnectQB from "../ButtonConnectQB";
import { StyledTooltip } from "../StyledTooltip";

type ButtonType = "connect" | "reconnect" | "disconnect";

/** Contains buttons (and logic) for managing the Quickbooks connection - connect, reconnect, and disconnect. */
export default function ConnectionButtons(props: {
  siteKey: string;
  qboRealmID: unknown;
}): JSX.Element {
  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );
  const [isBusy, setIsBusy] = useState<ButtonType | null>(null);

  async function handleClickConnect(): Promise<void> {
    let authURI: string;
    try {
      authURI = await DbWrite.qbo.connect(props.siteKey);
    } catch (e) {
      logger.error("QBO connection failure: rejection in getAuthURI", e);
      addToastMessage({
        id: createToastMessageID(),
        message: strings.QB_CANT_CONNECT,
        dialog: false,
        type: "error",
      });
      return;
    } finally {
      setIsBusy(null);
    }

    // Launch Popup using the JS window Object
    const parameters = `location=1,width=800,height=650,left=${(screen.width - 800) / 2},top=${(screen.height - 650) / 2}`;
    window.open(authURI, "connectPopup", parameters);
  }

  const connectButton = (
    <ButtonConnectQB
      isBusy={isBusy === "connect"}
      onClick={async () => {
        setIsBusy("connect");
        await handleClickConnect();
      }}
      type="button"
    />
  );

  const reconnectButton = (
    <StyledTooltip
      title={`If you see error messages such as "unable to refresh access token," try reconnecting. Disconnecting first is not necessary.`}
    >
      <BaseButtonSecondary
        isBusy={isBusy === "reconnect"}
        busyText="Reconnect"
        onClick={async () => {
          setIsBusy("reconnect");
          await handleClickConnect();
        }}
        type="button"
      >
        <SiQuickbooks />
        <span className="ml-2">{strings.RECONNECT}</span>
      </BaseButtonSecondary>
    </StyledTooltip>
  );

  const disconnectButton = (
    <StyledTooltip title="Disconnecting will stop Stilt from pushing more data to QuickBooks. No data will be lost. Stilt will continue syncing your data when the connection is established again.">
      <ButtonColored
        kind="danger"
        isBusy={isBusy === "disconnect"}
        busyText="Disconnect"
        onClick={async () => {
          setIsBusy("disconnect");
          try {
            await DbWrite.qbo.disconnect(props.siteKey);
            addToastMessage({
              id: createToastMessageID(),
              message: strings.DISCONNECTED,
              dialog: false,
              type: "success",
            });
          } catch (e) {
            logger.error("QBO disconnect failure", e);
            addToastMessage({
              id: createToastMessageID(),
              message: strings.QB_CANT_DISCONNECT,
              dialog: false,
              type: "error",
            });
            return;
          } finally {
            setIsBusy(null);
          }
        }}
      >
        <SiQuickbooks />
        <span className="ml-2">{strings.DISCONNECT}</span>
      </ButtonColored>
    </StyledTooltip>
  );

  if (typeof props.qboRealmID === "string" && props.qboRealmID.length) {
    return (
      <div className="flex flex-wrap gap-4">
        {reconnectButton}
        {disconnectButton}
      </div>
    );
  }
  return connectButton;
}
